import h from 'hyperscript';

import { logger } from 'cadenza/utils/logging';
import { appendOverflowDropdownButton } from 'cadenza/utils/toolbar-dropdown-util';
import { assert } from 'cadenza/utils/custom-error';

const name = 'd-legacy-toolbar';

export const SEPARATOR = 'd-legacy-toolbar-separator';

export class LegacyBaseToolbar extends HTMLElement {

  #options = {};
  #toolbarElements = this._createToolbarElements();

  constructor (options = {}) {
    super();
    this.#options = options;
    this.classList.add(name);
  }

  async connectedCallback () {
    const options = this.#options;
    this.hidden = options.hidden;

    const toolbarStructure = await this.addToolbarElements();
    this.createBindings();

    this.classList.toggle('is-resizable', options.isResizable);
    if (options.isResizable) {
      /**
       * We want to make sure there are equal number of buttons in the toolbar and in the toolbar-structure
       * (used when we move buttons from the toolbar to the dropdownButton when there's no space).
       * The logic previously was using toolbarStructure.getValues which gets the index of the last visibleNonSeparatorEntry.
       * This works as long as you're not setting to true the hidden field of some entry at the end of the toolbar-structure
       * ..like you would do if some of the actions would execute their isSupported and modified the corresponding button hidden state
       * before the equality size check below is run....this is precisely what happens because of our async/await usage
       * so we need this new getIndexOfLastNonSeparatorEntry method.
       */
      assert(this.children.length === toolbarStructure.getIndexOfLastNonSeparatorEntry(),
        'The created buttons and the underlying structure should have the same length');
      appendOverflowDropdownButton(this, toolbarStructure, this._getEndIndex());
    }
    this.addToolbarRightElements();
    this.lastToolbarWidth = this.getBoundingClientRect().width;
  }

  /**
   * Add all elements to the toolbar.
   * Override in subclass if content is available.
   *
   * @protected
   */
  async addToolbarElements () {
    // implemented in subclass
  }

  /**
   * Add elements on the right of dropdown button - these will always be visible
   *
   * @protected
   */
  addToolbarRightElements () {
    // implemented in subclass
  }

  /**
   * Extensibility point to create bindings between elements of the toolbar.
   * Override in subclass if needed.
   *
   * @protected
   */
  createBindings () {
    // implemented in subclass
  }

  /**
   * The index of the last (right most) toolbar element that is not moved to action dropdown menu
   * that contains all actions for which there is not enough horizontal space to display.
   * Override in subclass if not all elements can be moved to menu.
   *
   * @return {number} The index of the right most action that is not moved to collapsible action menu.
   *             -1 means all elements can be moved to the dropdown menu.
   */
  _getEndIndex () {
    return -1;
  }

  get toolbarElements () {
    return this.#toolbarElements;
  }

  get options () {
    return this.#options;
  }

  addButtons (...buttons) {
    this.#toolbarElements.add(buttons);
  }

  addSeparatorBeforeNextButton () {
    if (!isLastElementASeparator(this.#toolbarElements.elements)) {
      this.#toolbarElements.addSeparator = true;
    }
  }

  _createToolbarElements () {
    const toolbarElements = {
      elements: [],
      addSeparator: false,
      add: (...elements) => {
        const elementsToAdd = elements.flat();
        if (toolbarElements.addSeparator
          && (!elementsToAdd.length || !elementsToAdd[elementsToAdd.length - 1].classList.contains(SEPARATOR))) {
          elementsToAdd.unshift(h(`.${SEPARATOR}`));
          toolbarElements.addSeparator = false;
        }

        this.append(...elementsToAdd);
        toolbarElements.elements.push(...elementsToAdd);
      }
    };
    return toolbarElements;
  }

}

function isLastElementASeparator (elements) {
  return elements[elements.length - 1]?.classList.contains(SEPARATOR) ?? false;
}

export function updateButtonTitle (button, newText) {
  if (button.title && button.title !== newText) {
    button.title = newText;
    const btnTextAttrib = button.querySelector('.visuallyhidden');
    if (btnTextAttrib) {
      btnTextAttrib.textContent = newText;
    }
  }
}

export function toolbarButton (id, action) {
  const button = baseToolbarButton(id, action, [ action.icon, h('.visuallyhidden', action.label) ]);
  button.title = action.label;
  return button;
}

export function noTitleToolbarButton (id, action) {
  return baseToolbarButton(id, action, [ action.icon, h('.visuallyhidden', action.label) ]);
}

export function disableableToolbarButton (id, action) {
  const children = [ action.icon, h('.visuallyhidden', action.label) ];
  const button = h('button.button.button-borderless.button-icon', {
    type: 'button',
    id: `map-action--${id}`,
    onclick: () => action.execute()
  }, children);
  button.action = action;
  updateButtonTitle(button, action.label);
  button.title = action.label;
  return button;
}

export function baseToolbarButton (id, action, children) {
  const button = h('button.button.button-borderless.button-icon', {
    type: 'button',
    id: `map-action--${id}`,
    onclick: () => {
      action.isSupported().then(() => action.execute())
        .catch(reason => logger.error('Cannot execute action', reason));
    }
  }, children);
  button.action = action;
  const onChangeSupported = () => {
    action.isSupported()
      .then(supported => {
        button.disabled = !supported;
        updateButtonTitle(button, action.label);
      })
      .catch(() => {
        button.disabled = true;
      });
  };
  onChangeSupported();
  action.on('change:supported', onChangeSupported);
  return button;
}
